import dayjs, { Dayjs } from 'dayjs';

import ConvertUTCtoHHMM from './ConvertUTCtoHHMM';

/**
 * Change the date format YYYY-MM-DD function
 * @param {any} value date string
 * @returns {string} format string
 */
export const changeDateFormat = (value: string) => {
  if (value === '' || value === undefined) return '';
  const date = new Date(value);
  // const dateMDY = new Intl.DateTimeFormat('en-US', {year: 'numeric', month: '2-digit',day: '2-digit'}).format(date);
  const dateMDY = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return dateMDY;
};
/**
 * Change the date format YYYY-MM-DD HH:mm function
 * @param {any} value date string
 * @returns {string} format string
 */
export const changeDateTimeFormat = (value: string) => {
  if (value === '' || value === undefined) return '';
  const date = new Date(value);
  // const dateMDY = new Intl.DateTimeFormat('en-US', {year: 'numeric', month: '2-digit',day: '2-digit'}).format(date);
  const dateMDY = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return dateMDY;
};
/**
 * Remove the empty keys in object function
 * @param {any} inputObject input object
 * @returns {object} updated object
 */
export const removeEmptyKeys = (inputObject: any) => {
  return Object.fromEntries(
    Object.entries(inputObject).filter(
      (value) => value[1] !== '' && value[1] !== null && value[1] !== undefined,
    ),
  );
};

/**
 * Convert time strings (HH:MM) to minutes for numerical comparison
 * @param {any} time - The time
 */
const timeStringToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Function to check if the current time lies between opensAt and closesAt
 * @param {any} opensAt - Opening time in UTC
 * @param {any} closesAt - Closing time in UTC
 * @param {any} operatingHours - Optional daily operating hours object
 * @returns {boolean} - True if within operating hours
 */
export const isWithinOperatingHours = (
  opensAt: string,
  closesAt: string,
  operatingHours?: any,
): boolean => {
  if (operatingHours) {
    const dayNames = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ] as const;
    const currentDay = dayNames[new Date().getDay()];
    const dayData = operatingHours[currentDay];

    if (!dayData || dayData.status === 'closed') {
      return false;
    }

    const currentTime = ConvertUTCtoHHMM(new Date().toISOString());
    const openingTime = dayData.open || dayData.from;
    const closingTime = dayData.close || dayData.to;

    if (!openingTime || !closingTime) return false;

    const currentMinutes = timeStringToMinutes(currentTime);
    const openingMinutes = timeStringToMinutes(openingTime);
    const closingMinutes = timeStringToMinutes(closingTime);

    if (openingMinutes <= closingMinutes) {
      return currentMinutes >= openingMinutes && currentMinutes <= closingMinutes;
    }
    return currentMinutes >= openingMinutes || currentMinutes <= closingMinutes;
  }

  const currentTime = ConvertUTCtoHHMM(new Date().toISOString());
  const openingTime = ConvertUTCtoHHMM(opensAt);
  const closingTime = ConvertUTCtoHHMM(closesAt);

  const currentMinutes = timeStringToMinutes(currentTime);
  const openingMinutes = timeStringToMinutes(openingTime);
  const closingMinutes = timeStringToMinutes(closingTime);

  let withInOperationHours = false;

  // Check for normal or overnight case
  if (openingMinutes <= closingMinutes) {
    // Normal case: opens and closes on the same day
    withInOperationHours = currentMinutes >= openingMinutes && currentMinutes <= closingMinutes;
  } else {
    // Overnight case: opens late and closes early (e.g., 21:00 to 05:00)
    withInOperationHours = currentMinutes >= openingMinutes || currentMinutes <= closingMinutes;
  }
  return withInOperationHours;
};

/**
 * Split array
 * @param {any} array - The array
 * @param {any} chunkSize - The chunkSize
 */
export const splitArrayIntoChunks = (array: number[], chunkSize: number) => {
  const result = [];
  for (let index = 0; index < array.length; index += chunkSize) {
    result.push(array.slice(index, index + chunkSize));
  }
  return result;
};

/**
 * Convert the 12 hours to 24 hours
 * @param {any} time12Hour time 12 hour
 * @returns {string} time 24 hour
 */
export const convertTo24Hour: (value: string) => string = (time12Hour: string) => {
  const [time, modifier] = time12Hour.split(' ');
  const [hours, minutes] = time.split(':');

  let currentHours = parseInt(hours, 10);
  if (modifier === 'PM' && hours !== '12') {
    currentHours = parseInt(hours, 10) + 12;
  } else if (modifier === 'AM' && hours === '12') {
    currentHours = 0;
  }

  return `${String(currentHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Get the time format 24 to 12 hours
 * @param {any} time24 - The time24
 */
export const convertTo12HourFormat: (value: string) => string = (time24: string) => {
  const [hours, minutes] = time24.split(':');
  let currentHours = parseInt(hours, 10);

  const period = currentHours >= 12 ? 'PM' : 'AM';

  // Convert to 12-hour format
  currentHours = currentHours % 12 || 12; // Converts 0 to 12 for midnight

  return `${String(currentHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Get the current period time function
 * @returns {string} current period
 */
export const getPeriodCurrentTime: () => string = () => {
  const now = new Date();
  const hours = now.getHours();
  const period = hours >= 12 ? 'PM' : 'AM';

  return `${period}`;
};

/**
 * Get the current period time function
 * @param {any} value value string
 * @returns {string} current period
 */
export const getPeriodSelectCurrentTime: (value: string) => string = (value) => {
  const now = new Date(value);
  const hours = now.getHours();
  const period = hours >= 12 ? 'PM' : 'AM';

  return `${period}`;
};

/**
 * Time to change the hour and minutes
 * @param {any} time - Time string
 * @returns {object} Object containing hours and minutes
 */
export const parseTime = (time: string) => {
  const time12Hour = convertTo12HourFormat(time);
  // Split the string by space to separate time and period
  const [timePart, period] = time12Hour.split(' ');
  // Split the time part by colon to separate hours and minutes
  const [hour, minute] = timePart.split(':');
  return {
    hours: parseInt(hour, 10),
    minutes: parseInt(minute, 10),
    noon: period, // Period will be null for 24-hour format
  };
};

/**
 * get defferent time
 * @param {any} array - Array to group
 * @param {any} key - Key to group by
 * @returns {object} Mapped array
 */
export const arrayGroupBy = <T extends Record<string, any>>(
  array: T[],
  key: keyof T,
): Record<string, T[]> => {
  return array.reduce((result: Record<string, T[]>, currentItem) => {
    const outputResult = result;
    const group = currentItem[key] as string; // Ensure it's treated as a string key
    if (!outputResult[group]) {
      outputResult[group] = [];
    }
    outputResult[group].push(currentItem);
    return outputResult;
  }, {});
};
/**
 * Array convert to the group
 * @param {any} startTime start time string
 * @param {any} endTime end time string
 * @returns {string} time difference string
 */
export const getTimeBetweenTime = (startTime: string, endTime: string) => {
  console.log(startTime, endTime);
  // Create date objects for the two times
  const start = new Date(startTime);
  const end = new Date(endTime);

  // Calculate the difference in milliseconds
  const diffInMs = end.getTime() - start.getTime();

  // Convert to minutes
  const diffInMinutes = diffInMs / (1000 * 60);
  return `${Math.floor(diffInMinutes / 60)}H ${(diffInMinutes % 60).toFixed(2)}Min`;
};

/**
 * getDateRange
 * @param {any} type - The type
 */
export function getDateRange(type: string): { startDate: Dayjs; endDate: Dayjs } {
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');

  switch (type) {
    case 'today':
      return {
        startDate: today,
        endDate: today,
      };
    case 'yesterday':
      return {
        startDate: yesterday,
        endDate: today,
      };
    case 'week':
      return {
        startDate: today.subtract(6, 'day'), // Last 7 days (including today)
        endDate: today,
      };
    case 'month':
      return {
        startDate: today.subtract(29, 'day'), // Last 30 days (including today)
        endDate: today,
      };
    case 'year':
      return {
        startDate: today.subtract(364, 'day'), // Last 365 days (including today)
        endDate: today,
      };
    default:
      return {
        startDate: today,
        endDate: today,
      };
  }
}

/**
 * Function to format minutes in to hours and minutes
 * @param {any} totalMinutes total minutes
 * @returns {string} formatted hours and minutes
 */
export function formatMinutesToHoursAndMinutes(totalMinutes: number): string {
  if (totalMinutes < 0 || !Number.isFinite(totalMinutes)) {
    console.error('Invalid input: totalMinutes must be a non-negative number');
    return '';
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60); // round to avoid decimals

  const hoursString = hours > 0 ? `${hours} hr${hours > 1 ? 's' : ''}` : '';
  const minutesString = minutes > 0 ? `${minutes} min${minutes > 1 ? 's' : ''}` : '';

  return [hoursString, minutesString].filter(Boolean).join(' ');
}
/**
 * Array convert to the date control
 * @param {any} dateString date string
 * @returns {string} relative time
 */
export function getRelativeTime(dateString: string): string {
  const now = new Date();
  const past = new Date(dateString);
  const diff = now.getTime() - past.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hr${hours > 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} d${days > 1 ? 's' : ''} ago`;
  if (weeks < 4) return `${weeks} w${weeks > 1 ? 's' : ''} ago`;
  if (months < 12) return `${months} mo${months > 1 ? 's' : ''} ago`;
  return `${years} y${years > 1 ? 's' : ''} ago`;
}
